import { useState } from 'react';
import { useAxios } from '../core/_axios';
import useLocalizedCopy from './useLocalizedCopy';
import { useAuth } from '../AuthContext';

interface SignatureSearchResult {
  id: number;
  unique_identifier: string;
  signer_name: string;
  signer_email: string;
  service_name: string;
  service_id: number;
  service_unique_identifier: string;
  created_at: string;
  has_email_campaign: boolean;
  account_name?: string;
}

interface SearchSignaturesResponse {
  success: boolean;
  signatures: SignatureSearchResult[];
  error?: string;
}

interface CreateManualCampaignResponse {
  success: boolean;
  data?: {
    message: string;
    campaign?: {
      id: string;
      [key: string]: any;
    };
  };
  error?: string;
}

export const useManualEmailCampaign = () => {
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchResults, setSearchResults] = useState<SignatureSearchResult[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const copy = useLocalizedCopy();
  const axios = useAxios();
  const { viewingAllAccounts } = useAuth();

  const searchSignatures = async (searchTerm: string, includeNoEmail = false) => {
    setIsSearching(true);
    setSearchError(null);
    try {
      const response = await axios.get<SearchSignaturesResponse>(
        '/authenticated/current-account/email-campaigns/search-signatures',
        {
          params: {
            q: searchTerm,
            include_no_email: includeNoEmail,
            all_accounts: viewingAllAccounts
          }
        }
      );

      if (response.data.success) {
        setSearchResults(response.data.signatures);
      } else {
        console.error('Search error:', response.data.error);
        setSearchResults([]);
        setSearchError(response.data.error || 'Failed to search signatures');
      }
    } catch (error: any) {
      console.error('Error searching signatures:', error);
      setSearchResults([]);
      const errorMessage = error.response?.data?.error || 'Failed to search signatures';
      setSearchError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const fetchRecentSignatures = async (includeNoEmail = false) => {
    setIsSearching(true);
    setSearchError(null);
    try {
      const response = await axios.get<SearchSignaturesResponse>(
        '/authenticated/current-account/email-campaigns/search-signatures',
        {
          params: {
            q: '', // Empty search term to get recent signatures
            include_no_email: includeNoEmail,
            all_accounts: viewingAllAccounts
          }
        }
      );

      if (response.data.success) {
        setSearchResults(response.data.signatures);
      } else {
        console.error('Error fetching recent signatures:', response.data.error);
        setSearchResults([]);
        setSearchError(response.data.error || 'Failed to fetch recent signatures');
      }
    } catch (error: any) {
      console.error('Error fetching recent signatures:', error);
      setSearchResults([]);
      const errorMessage = error.response?.data?.error || 'Failed to fetch recent signatures';
      setSearchError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const createManualCampaign = async (signatureId: number) => {
    setIsCreating(true);
    try {
      const response = await axios.post<CreateManualCampaignResponse>(
        '/authenticated/current-account/email-campaigns/create-manual',
        { signature_id: signatureId }
      );

      if (response.data.success) {
        // Update the search results to mark this signature as having a campaign
        setSearchResults(prev => 
          prev.map(sig => 
            sig.id === signatureId 
              ? { ...sig, has_email_campaign: true }
              : sig
          )
        );
        
        // Extract campaign ID if available
        const campaignId = response.data.data?.campaign?.id;
        console.log('[useManualEmailCampaign] Campaign created, response:', response.data);
        console.log('[useManualEmailCampaign] Extracted campaign ID:', campaignId);
        
        return { 
          success: true, 
          message: 'Message created successfully',
          campaignId: campaignId 
        };
      } else {
        return { success: false, error: response.data.error || 'Failed to create message' };
      }
    } catch (error) {
      console.error('Error creating manual campaign:', error);
      return { success: false, error: 'Failed to create message' };
    } finally {
      setIsCreating(false);
    }
  };

  const clearResults = () => {
    setSearchResults([]);
  };

  return {
    searchSignatures,
    fetchRecentSignatures,
    createManualCampaign,
    clearResults,
    searchResults,
    searchError,
    isSearching,
    isCreating
  };
};